import { useState, useEffect, useRef } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getArticleById } from "src/api/helpCenter";
import { ContactBanner } from "./ContactBanner";
import { reactToArticle } from "src/api/helpCenter";
import { HelpCenterArticleSearchBar } from "./searchBar.jsx";
import { ArticleCard } from "./ArticleCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ArrowLeftIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

const Breadcrumb = ({ title }) => {
  const navigate = useNavigate();

  const handleBackClick = (e) => {
    e.preventDefault();
    navigate("/help");
  };

  return (
    <div className="flex md:flex-row flex-col   items-center md:justify-between gap-1.5 text-sm text-gray-500 px-8 ">
      <div className="flex items-center gap-1.5">
        <ArrowLeftIcon />
        <button
          href="/help"
          className="hover:underline"
          onClick={handleBackClick}
        >
          Back to Help Center
        </button>
        {title && (
          <>
            <span className="text-gray-400">/</span>
            <span className="text-orange-500 font-medium">{title}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 w-full md:w-64 relative">
        <HelpCenterArticleSearchBar />
      </div>
    </div>
  );
};

const HelpCenterArticleDetails = () => {
  const allArticles = useSelector((state) => state.helpCenterArticles);

  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [similarArticles, setSimilarArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("");
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const contentRefs = useRef({});
  const observerRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [reactionSubmitted, setReactionSubmitted] = useState(false);

  const navigate = useNavigate();
  // Fetch article on component mount
  useEffect(() => {
    const fetchArticleData = async () => {
      try {
        setLoading(true);
        const articleData = await getArticleById(articleId);
        setArticle(articleData);
        setReactionSubmitted(false);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to load article");
        console.error("Error fetching article:", err);
      } finally {
        setLoading(false);
      }
    };

    if (articleId) {
      fetchArticleData();
    }
  }, [articleId]);

  // Update similar articles when article or allArticles changes
  useEffect(() => {
    if (article && allArticles) {
      const filteredSimilar = allArticles.filter(
        (a) =>
          a._id !== article._id &&
          a.category === article.category &&
          a.role === article.role,
      );
      setSimilarArticles(filteredSimilar);
    }
  }, [article, allArticles]);

  // Set initial section when article loads
  useEffect(() => {
    if (article?.content?.length > 0) {
      setActiveSection(
        article.content[0].topic.toLowerCase().replace(/\s+/g, "-"),
      );
    }
  }, [article]);

  // Check if user is logged in
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    setIsLoggedIn(!!authToken);
  }, []);

  // Setup Intersection Observer for auto-highlight
  useEffect(() => {
    const options = {
      root: null, // Watch the entire viewport
      rootMargin: "-20% 0px -60% 0px", // Fine-tune the detection zone or shrinks the detection area to the middle portion of the viewport, ensuring that a section is only considered active when it's prominently visible.
      threshold: 0, // Trigger when even 1 pixel is visible
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // it is a spy tool that watches the content sections and tells us which one is currently visible in the viewport based on the defined options. Whenever a section becomes visible, it updates the activeSection state to highlight the corresponding item in the sidebar.
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options); //when the section enters the watch zone, update the sidebar to highliht that section.

    // Observe all content sections
    Object.values(contentRefs.current).forEach((ref) => {
      // start syping on every section in the article.
      if (ref) {
        observerRef.current.observe(ref);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [article]);

  // Handle sidebar click and scroll
  const handleSidebarClick = (sectionId) => {
    const element = contentRefs.current[sectionId];
    if (element) {
      setActiveSection(sectionId);
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleSimilarArticleClick = () => {
    const urlRole = article.role.split(" ").join("%20");
    const urlCategory = article.category.split(" ").join("%20");
    navigate(`/help/by-role?role=${urlRole}&category=${urlCategory}`);
  };

  const handleReaction = async (reaction) => {
    try {
      setReactionLoading(true);
      await reactToArticle(articleId, reaction);
      setReactionSubmitted(true);
    } catch (err) {
      console.error("Error reacting to article:", err);
      alert("Failed to submit your response. Please try again.");
    } finally {
      setReactionLoading(false);
    }
  };

  // Generate table of contents from article content
  const tableOfContents =
    article?.content?.map((item) => ({
      id: item.topic.toLowerCase().replace(/\s+/g, "-"),
      label: item.topic,
    })) || [];

  // Add FAQs to table of contents
  if (article?.FAQs?.length > 0) {
    tableOfContents.push({ id: "faqs", label: "FAQs" });
  }

  return (
    <div className="min-h-screen mt-28 bg-white">
      {loading && (
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Loading article...</p>
        </div>
      )}

      {error && (
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <p className="text-red-500">Error: {error}</p>
        </div>
      )}

      {!loading && !error && article && (
        <>
          {/* Breadcrumb */}
          <Breadcrumb title={article.title} />

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Page Title */}
            <h1 className="text-4xl font-bold mb-4">{article?.title}</h1>
            <p className="text-gray-600 mb-8">{article?.description}</p>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sticky Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-20 bg-white">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-2xl  mb-4 text-gray-900">
                      Content
                    </h3>
                    <nav className="space-y-2">
                      {tableOfContents.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleSidebarClick(item.id)}
                          className={`w-full text-left px-3 py-2 rounded font-bold transition-colors ${
                            activeSection === item.id
                              ? "bg-red-50 text-orange-500  rounded-lg pl-6 border-orange-500"
                              : "text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="lg:col-span-3">
                <div className="space-y-8">
                  {/* Dynamically Render Content Sections */}
                  {article?.content?.map((item, index) => {
                    const sectionId = item.topic
                      .toLowerCase()
                      .replace(/\s+/g, "-");
                    return (
                      <section
                        key={index}
                        ref={(el) => (contentRefs.current[sectionId] = el)}
                        id={sectionId}
                        className="scroll-mt-20 flex justify-start gap-6 items-start"
                      >
                        <div
                          className={`w-2 h-8 ${activeSection === sectionId ? "bg-orange-500" : "bg-orange-300"} rounded-r-lg flex-shrink-0`}
                        ></div>
                        <div className="pl-4">
                          <h2 className={`text-2xl font-bold mb-4`}>
                            {item.topic}
                          </h2>
                          <div className="text-gray-600 mb-4">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                p: ({ node, ...props }) => (
                                  <p className="my-3" {...props} />
                                ),
                                ol: ({ node, ...props }) => (
                                  <ol
                                    className="my-3 ml-6 list-decimal"
                                    {...props}
                                  />
                                ),
                                ul: ({ node, ...props }) => (
                                  <ul
                                    className="my-3 ml-6 list-disc"
                                    {...props}
                                  />
                                ),
                                li: ({ node, ...props }) => (
                                  <li className="my-1" {...props} />
                                ),
                                h1: ({ node, children, ...props }) => (
                                  <h1
                                    className="text-xl font-bold my-3"
                                    {...props}
                                  >
                                    {children}
                                  </h1>
                                ),
                                h2: ({ node, children, ...props }) => (
                                  <h2
                                    className="text-lg font-bold my-3"
                                    {...props}
                                  >
                                    {children}
                                  </h2>
                                ),
                                h3: ({ node, children, ...props }) => (
                                  <h3
                                    className="text-base font-bold my-2"
                                    {...props}
                                  >
                                    {children}
                                  </h3>
                                ),
                                strong: ({ node, ...props }) => (
                                  <strong className="font-bold" {...props} />
                                ),
                                em: ({ node, ...props }) => (
                                  <em className="italic" {...props} />
                                ),
                              }}
                            >
                              {item.description}
                            </ReactMarkdown>
                          </div>
                          {item.note && (
                            <div className="bg-gray-50 border-l-4 border-gray-300 pl-4 py-3 text-sm text-gray-700">
                              <strong>Note:</strong> {item.note}
                            </div>
                          )}
                        </div>
                      </section>
                    );
                  })}

                  {/* FAQs Section */}
                  {article?.FAQs?.length > 0 && (
                    <section
                      ref={(el) => (contentRefs.current["faqs"] = el)}
                      id="faqs"
                      className="scroll-mt-20"
                    >
                      <h2 className="text-2xl font-bold mb-6">FAQs</h2>
                      <div className="space-y-4">
                        {article.FAQs.map((faq, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-4"
                          >
                            <button
                              onClick={() =>
                                setExpandedFAQ(
                                  expandedFAQ === index ? null : index,
                                )
                              }
                              className="w-full flex items-center justify-between"
                            >
                              <span className="font-bold text-gray-900 text-left">
                                {faq.question}
                              </span>
                              <span className="text-orange-500 text-xl">
                                {expandedFAQ === index ? "−" : "+"}
                              </span>
                            </button>
                            {expandedFAQ === index && (
                              <p className="text-gray-600 text-sm mt-3 leading-relaxed">
                                {faq.answer}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {isLoggedIn && article.userReacted === false && (
                    <div className="w-full flex justify-center items-center">
                      <div className="mt-12 bg-[#F7F3F2] rounded-2xl px-6 py-3 w-full md:w-auto">
                        {!reactionSubmitted ? (
                          <div className="flex justify-between items-center gap-3 flex-col md:flex-row">
                            <p className="text-gray-900 font-bold text-lg">
                              Was this article helpful?
                            </p>
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleReaction("like")}
                                disabled={reactionLoading}
                                className="px-6 py-2 bg-white rounded-xl hover:bg-gray-50 font-medium text-sm flex items-end gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                              >
                                <span className="font-bold">
                                  {reactionLoading ? "Submitting..." : "Yes"}
                                </span>
                                <ThumbsUp className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleReaction("dislike")}
                                disabled={reactionLoading}
                                className="px-6 py-2 bg-white rounded-xl hover:bg-gray-50 font-medium text-sm flex items-end gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                              >
                                <span className="font-bold">
                                  {reactionLoading ? "Submitting..." : "No"}
                                </span>
                                <ThumbsDown className="w-5 h-5 bg-transparent text-black" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-center items-center gap-2 text-black">
                            <p className="font-bold text-lg">
                              ✓ Thanks for your response!
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Similar Articles */}
                  <div className="mt-12">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-2xl font-bold">Similar Articles</h3>
                      <button
                        className="text-black font-medium text-sm"
                        onClick={handleSimilarArticleClick}
                      >
                        View all
                      </button>
                    </div>
                    <div className="overflow-x-auto scrollbar-hide">
                      <div className="flex gap-6 pb-4">
                        {similarArticles.length > 0 ? (
                          similarArticles.map((article) => (
                            <div
                              key={article._id}
                              className="flex-shrink-0 w-72"
                            >
                              <ArticleCard article={article} />
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500">
                            No similar articles found
                          </p>
                        )}
                      </div>
                    </div>
                    <style>{`
                .scrollbar-hide {
                  -ms-overflow-style: none;
                  scrollbar-width: none;
                }
                .scrollbar-hide::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
                  </div>

                  {/* Contact Section */}
                  <ContactBanner />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HelpCenterArticleDetails;
